import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Browser speech-to-text wrapper around the Web Speech API (`SpeechRecognition`).
 *
 * The backend takes **text only** (docs/ai-assistant-api.md §2) — the mic button
 * transcribes locally, then the transcript is sent like a typed message. Feature-detected:
 * {@link isSupported} is false in browsers without the API (e.g. Firefox), where the UI
 * simply hides the mic button.
 *
 * Device handling: before each session we prime the microphone via `getUserMedia`, which
 * (a) reuses a previously-granted permission so the user isn't re-prompted every time and
 * (b) remembers the chosen audio device in localStorage and reuses it next session, falling
 * back to the system default if it's been unplugged. Caveat: the Web Speech API itself only
 * captures from the *default* device — priming reduces friction and persists the preference
 * but cannot force transcription through a hand-picked non-default mic.
 */

type SpeechRecognitionCtor = new () => any;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

/** localStorage key for the user's preferred microphone (persists across sessions). */
const PREFERRED_MIC_KEY = 'ai-assistant:preferred-mic-id';

export interface VoiceTranscript {
  text: string;
  /** False while the user is still speaking (interim result), true on the final result. */
  isFinal: boolean;
}

@Injectable({ providedIn: 'root' })
export class AiAssistantVoiceService {
  private recognition: any = null;

  constructor(private readonly zone: NgZone) {}

  get isSupported(): boolean {
    return getSpeechRecognitionCtor() != null;
  }

  /**
   * Start listening. Emits interim + final transcripts, then completes when speech ends
   * or {@link stop} is called. Errors propagate so the caller can surface mic/permission issues.
   */
  listen(lang = 'en-AU'): Observable<VoiceTranscript> {
    return new Observable<VoiceTranscript>(observer => {
      const Ctor = getSpeechRecognitionCtor();
      if (!Ctor) {
        observer.error(new Error('Speech recognition not supported in this browser.'));
        return;
      }

      const recognition = new Ctor();
      this.recognition = recognition;
      recognition.lang = lang;
      recognition.interimResults = true;
      recognition.continuous = false;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event: any) => {
        let text = '';
        let isFinal = false;
        for (let i = event.resultIndex; i < event.results.length; i++) {
          text += event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            isFinal = true;
          }
        }
        // SpeechRecognition callbacks fire outside Angular's zone.
        this.zone.run(() => observer.next({ text: text.trim(), isFinal }));
      };

      recognition.onerror = (event: any) => {
        this.zone.run(() => observer.error(new Error(event?.error || 'speech-recognition-error')));
      };

      recognition.onend = () => {
        this.zone.run(() => observer.complete());
      };

      // Prime the preferred microphone + permission BEFORE starting recognition. This
      // remembers the chosen device for future sessions and lets the browser reuse a
      // previously-granted permission (no repeat prompt). NOTE: the Web Speech API always
      // transcribes from the system default mic — priming can't reroute it to a non-default
      // device, but it does cut the per-use device/permission friction.
      let cancelled = false;
      this.prepareMic()
        .catch(() => {
          /* Non-fatal: fall back to the default device. If permission is truly denied,
             recognition.start() below surfaces the same `not-allowed` error to the caller. */
        })
        .then(() => {
          if (cancelled) {
            return;
          }
          try {
            recognition.start();
          } catch (err) {
            observer.error(err);
          }
        });

      return () => {
        cancelled = true;
        this.stop();
      };
    });
  }

  /** Audio input devices currently available (labels populate after permission is granted). */
  async listAudioInputDevices(): Promise<MediaDeviceInfo[]> {
    const md = this.mediaDevices();
    if (!md?.enumerateDevices) {
      return [];
    }
    try {
      const devices = await md.enumerateDevices();
      return devices.filter(d => d.kind === 'audioinput');
    } catch {
      return [];
    }
  }

  /**
   * Acquire the mic once to grant/persist permission and remember which device was used.
   * Reuses the stored preference when that device is still connected; otherwise falls back
   * to the system default (and re-records whatever the browser actually granted).
   */
  private async prepareMic(): Promise<void> {
    const md = this.mediaDevices();
    if (!md?.getUserMedia) {
      return;
    }

    const useDeviceId = await this.resolvePreferredDeviceId();
    const constraints: MediaStreamConstraints = {
      // `ideal` (not `exact`) so a missing/changed device degrades to the default
      // instead of throwing — keeps voice working without an extra prompt.
      audio: useDeviceId ? { deviceId: { ideal: useDeviceId } } : true,
    };

    let stream: MediaStream | null = null;
    try {
      stream = await md.getUserMedia(constraints);
      const granted = stream.getAudioTracks()[0]?.getSettings?.().deviceId;
      if (granted) {
        this.setPreferredDeviceId(granted);
      }
    } finally {
      // We only needed the grant + device id; SpeechRecognition does its own capture.
      stream?.getTracks().forEach(track => track.stop());
    }
  }

  /** Stored device id, but only if it's still present in the device list (else null). */
  private async resolvePreferredDeviceId(): Promise<string | null> {
    const preferred = this.getPreferredDeviceId();
    if (!preferred) {
      return null;
    }
    const devices = await this.listAudioInputDevices();
    // Before the first grant, ids may be hidden; treat an empty list as "can't tell" and
    // still try the preference (it degrades via `ideal`).
    if (!devices.length) {
      return preferred;
    }
    return devices.some(d => d.deviceId === preferred) ? preferred : null;
  }

  private mediaDevices(): MediaDevices | null {
    return typeof navigator !== 'undefined' && navigator.mediaDevices ? navigator.mediaDevices : null;
  }

  private getPreferredDeviceId(): string | null {
    try {
      return localStorage.getItem(PREFERRED_MIC_KEY);
    } catch {
      return null;
    }
  }

  private setPreferredDeviceId(id: string): void {
    try {
      localStorage.setItem(PREFERRED_MIC_KEY, id);
    } catch {
      /* localStorage unavailable (private mode) — preference is best-effort */
    }
  }

  stop(): void {
    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch {
        /* already stopped */
      }
      this.recognition = null;
    }
  }
}
